/**
 * Navigation utilities for the application
 * This file provides helper functions for navigation that are more maintainable
 * than direct window.location changes throughout the codebase
 */
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Define all app routes in a central location
export const ROUTES = {
  HOME: '/',
  AUTH: '/auth',
  ACCOUNT_SETUP: '/account-setup',
  SCHEDULE_PICKUP: '/schedule-pickup',
  STORE_DETAILS: '/schedule-pickup/store-details',
  ORDER_DETAILS: '/schedule-pickup/order-details',
  DELIVERY_DETAILS: '/schedule-pickup/delivery-details',
  PAYMENT: '/schedule-pickup/payment',
  COMPLETE: '/schedule-pickup/complete',
  RETURNS: '/returns',
  RETURNS_STORE_DETAILS: '/returns/store-details',
  RETURNS_DETAILS: '/returns/details',
  RETURNS_REVIEW: '/returns/review',
  RETURNS_AUTHORIZE: '/returns/authorize',
  RETURNS_SCHEDULE: '/returns/schedule',
  RETURNS_TRACK: '/returns/track',
  RETURNS_PROOF: '/returns/proof',
  RETURNS_REFUND: '/returns/refund',
  RETURNS_COMPLETE: '/returns/complete',
  RETURNS_ORDER_DETAILS: '/returns/details',
  RETURNS_DELIVERY_DETAILS: '/returns/schedule',
  RETURNS_PAYMENT: '/returns/authorize',
  MEMBER_RETURNS: '/member/returns',
  MEMBER_RETURNS_STORE_DETAILS: '/member/returns/store-details',
  PICKUP_HISTORY: '/pickup-history',
  STORE_SELECTION: '/store-selection',
  WALLET: '/wallet',
  SETTINGS: '/settings',
  HISTORY: '/pickup-history',
  LOGOUT: '/logout',
  HELP_CENTER: '/help-center',
  CURBSIDE_PICKUP: "/curbside-pickup",
  API_LANDING: "/landing",
  ROBOT_LEASING: "/robot-leasing"
};

/**
 * Checks if the current route exactly matches the specified path.
 * @param path - The path to check (e.g., "/schedule-pickup").
 * @returns true if the current route matches exactly, otherwise false.
 */
export function isCurrentRoute(path: string): boolean {
  const location = window.location.pathname;
  return location === path;
}

/**
 * Checks if the current route starts with the specified path.
 * @param path - The path prefix to check (e.g., "/schedule").
 * @returns true if the current route starts with the given path, otherwise false.
 */
export function routeStartsWith(path: string): boolean {
  const location = window.location.pathname;
  return location.startsWith(path);
}

/**
 * Function to construct a URL with query parameters
 * @param route The base route
 * @param params Optional query parameters
 * @returns The complete URL string
 */
export function buildUrl(route: string, params?: Record<string, string | number>): string {
  let queryString = '';
  if (params && Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      searchParams.append(key, String(value));
    });
    queryString = `?${searchParams.toString()}`;
  }
  
  return `${route}${queryString}`;
}

/**
 * React hook to navigate to a route. Must be used within a component.
 * @param route The route to navigate to
 * @param params Optional query parameters
 */
export function useNavigateTo() {
  const navigate = useNavigate();
  
  return (route: string, params?: Record<string, string | number>) => {
    const url = buildUrl(route, params);
    navigate(url);
  };
}

/**
 * Simple navigation function for use in non-component code or event handlers
 * Uses window.location.href instead of React Router's navigate
 * @param route The route to navigate to
 * @param params Optional query parameters
 */
export function navigateTo(route: string, params?: Record<string, string | number>): void {
  const url = buildUrl(route, params);
  window.location.href = url;
}

/**
 * Wrapper for navigateTo with explicit params support to maintain compatibility
 * with existing code.
 * 
 * @param route The base route
 * @param params Object containing query parameters
 */
export function navigateWithParams(route: string, params: Record<string, string | number>): void {
  navigateTo(route, params);
}

/**
 * Hook to set up navigation listeners and handlers
 */
export function useNavigationSetup() {
  const location = useLocation();

  useEffect(() => {
    // Add any global navigation handlers or listeners here

    // Handle cases where direct URLs are accessed
    if (location.pathname.includes('/schedule-pickup')) {
      // Ensure proper state is loaded for deep linking
    }
  }, [location]);
}

/**
 * Get URL query parameters as an object
 * @returns Object containing all query parameters
 */
export const getQueryParams = (): Record<string, string> => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const params: Record<string, string> = {};

  Array.from(urlSearchParams.entries()).forEach(([key, value]) => {
    params[key] = value;
  });

  return params;
};

/**
 * Get a specific query parameter
 * @param name The name of the parameter to get
 * @returns The parameter value or null if not found
 */
export const getQueryParam = (name: string): string | null => {
  return new URLSearchParams(window.location.search).get(name);
};

/**
 * Store data in session for the next page
 * More reliable than query parameters for complex data
 * @param key The key to store the data under
 * @param data The data to store
 */
export const storePageData = (key: string, data: any): void => {
  sessionStorage.setItem(key, JSON.stringify(data));
};

/**
 * Retrieve data stored for the current page
 * @param key The key the data was stored under
 * @returns The stored data, or null if not found
 */
export const getPageData = <T>(key: string): T | null => {
  const data = sessionStorage.getItem(key);

  if (!data) {
    return null;
  }

  try {
    return JSON.parse(data) as T;
  } catch (error) {
    console.error(`Error parsing stored data for key "${key}":`, error);
    return null;
  }
};

/**
 * Clear stored page data
 * @param key The key to clear, or undefined to clear all page data
 */
export const clearPageData = (key?: string): void => {
  if (key) {
    sessionStorage.removeItem(key);
  } else {
    sessionStorage.clear();
  }
};
